<template>
  <div class="flex flex-grow flex-col justify-around gap-8 pt-6">
    <div class="flex flex-col gap-2">
      <ExceptionCard :title="titleText" :message="messageText" />

      <MoProductCard
        class="mt-9"
        v-if="props.type === 'already-attested'"
        :card-data="moProductCardData"
      />
    </div>

    <div class="flex flex-col gap-4">
      <VButton type="solid" :title="buttonText" :callback="onBtnClick" />

      <VButton
        v-for="webhook in webhooks"
        :key="webhook.id"
        type="outline"
        :title="getReexecuteButtonText(webhook.name)"
        :disabled="timeToReexecute > 0"
        :callback="async () => onBtnClickToReexecuteWebhook(webhook.id)"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import VButton from '@/components/Button/VButton.vue'
import ExceptionCard from '@/components/ExceptionCard/ExceptionCard.vue'
import MoProductCard from '@/components/MoProductCard/MoProductCard.vue'
import { useAppStore } from '@/stores/app.store'
import { useUserStore } from '@/stores/user.store'
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { useListWebhooks, useReexecuteWebhook } from './query'

const appStore = useAppStore()
const userStore = useUserStore()

const webhookToRun = ref('')

const activityId = computed(() => userStore.decodedToken?.activity.id ?? '')

const { data: webhooks, refetch: fetchWebhooks } = useListWebhooks(activityId.value)
const { refetch: fetchReexecuteWebhook, isError: reexecuteWebhookGotError } = useReexecuteWebhook(
  appStore.currentTag!,
  webhookToRun
)

const reexecutionTimeBlock = 60

const timeToReexecute = ref(0)

const getReexecuteButtonText = (webhookName: string) => {
  return timeToReexecute.value > 0
    ? `Aguarde ${timeToReexecute.value} segundos`
    : `Executar ${webhookName}`
}

const onBtnClickToReexecuteWebhook = async (webhookId: string) => {
  webhookToRun.value = webhookId
  await fetchReexecuteWebhook()

  if (reexecuteWebhookGotError.value) return

  timeToReexecute.value = reexecutionTimeBlock

  const interval = setInterval(() => {
    if (timeToReexecute.value > 0) {
      timeToReexecute.value -= 1
    } else {
      clearInterval(interval)
    }
  }, 1000)
}

onMounted(async () => {
  await fetchWebhooks()
})

const emit = defineEmits(['clear-exception'])

const buttonText = computed(() => {
  return {
    'ended-op': 'Ler nova OP',
    'already-attested': 'Ler nova TAG'
  }[props.type]
})

const titleText = computed(() => {
  return {
    'ended-op': 'Fim da atividade',
    'already-attested': 'Este produto já foi apontado'
  }[props.type]
})

const messageText = computed(() => {
  return {
    'ended-op': 'Esta OP já possui todos os apontamentos esperados.',
    'already-attested': 'Faça a leitura de outra TAG.'
  }[props.type]
})

type Props = {
  type: 'ended-op' | 'already-attested'
}

const props = withDefaults(defineProps<Props>(), {})

const moCode = ref('')
moCode.value = appStore.currentManufacturingOrder?.orderNumber.toString() ?? ''

const onBtnClick = async () => {
  if (props.type === 'ended-op') {
    appStore.actions.setManufacturingOrder(null)
    appStore.actions.clearCurrentTag()
    emit('clear-exception')
  } else if (props.type === 'already-attested') {
    appStore.actions.clearCurrentTag()
    emit('clear-exception')
  }
}

console.log(appStore)
const moProductCardData = computed(() =>
  appStore.currentManufacturingOrder
    ? {
        imageUrl: appStore.currentManufacturingOrder.imageUrl,
        productUnitCode: appStore.currentManufacturingOrder.productUnitCode,
        primaryColorHex: appStore.currentManufacturingOrder.primaryColorHex,
        secondaryColorHex: appStore.currentManufacturingOrder.secondaryColorHex,
        description: appStore.currentManufacturingOrder.description,
        size: appStore.currentManufacturingOrder.size,
        year: appStore.currentManufacturingOrder.year,
        checked: appStore.currentManufacturingOrder.checked,
        problem: appStore.currentManufacturingOrder.problem,
        total: appStore.currentManufacturingOrder.total,
        loadedAt: appStore.currentMoProduct?.loadedAt
      }
    : null
)

onUnmounted(async () => {
  appStore.actions.setCurrentMoProduct(null)
  appStore.actions.clearCurrentTag()
  emit('clear-exception')
})
</script>
